import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(3);
  const [isNewRegistration, setIsNewRegistration] = useState(false); // Track if it's a new registration
  
  // Ref to prevent double-call in React StrictMode
  const hasVerified = useRef(false);

  useEffect(() => {
    const token = searchParams.get("token");
    if (token && !hasVerified.current) {
      hasVerified.current = true;
      verifyEmail(token);
    } else if (!token) {
      setStatus("error");
      setMessage("Invalid verification link. Please check your email for the correct link.");
    }
  }, [searchParams]);

  // Auto-redirect countdown after successful NEW REGISTRATION verification only
  useEffect(() => {
    if (status === "success" && isNewRegistration && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === "success" && isNewRegistration && countdown === 0) {
      navigate("/dashboard", { replace: true });
    }
  }, [status, countdown, navigate, isNewRegistration]);

  const verifyEmail = async (verificationToken) => {
    // Try registration verification first (tokens start with "verify_")
    // Then try additional email verification if that fails
    
    try {
      // First, try the registration verification endpoint
      const response = await axios.get(`${API}/v2/auth/verify-email?token=${verificationToken}`);
      
      // Extract JWT token and user data from response
      const { token: jwtToken, user } = response.data;
      
      // Save JWT token and log the user in
      if (jwtToken) {
        localStorage.setItem("token", jwtToken);
        setToken(jwtToken);
      }
      
      if (user) {
        setUser(user);
        setEmail(user.email);
      }
      
      setIsNewRegistration(true); // Enable auto-redirect for new registrations
      setStatus("success");
      setMessage(response.data.message || "Your email has been verified successfully!");
    } catch (authError) {
      // If registration verification fails, try additional email verification
      try {
        const additionalResponse = await axios.get(`${API}/v2/users/emails/verify?token=${verificationToken}`);
        
        setEmail(additionalResponse.data.email || "");
        setIsNewRegistration(false); // Don't auto-redirect for additional emails
        setStatus("success");
        setMessage(additionalResponse.data.message || "Your additional email has been verified!");
      } catch (additionalError) {
        // Both failed - show the error
        setStatus("error");
        const errorDetail = additionalError.response?.data?.detail || 
                           authError.response?.data?.detail || 
                           "Verification failed. The link may be invalid or expired.";
        setMessage(errorDetail);
      }
    }
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <Card className="max-w-md w-full bg-white/95 backdrop-blur border-0 shadow-2xl">
        <CardContent className="p-8 text-center">
          {status === "verifying" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Verifying Your Email...
              </h2>
              <p className="text-slate-600">Please wait while we verify your email address.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Email Verified!
              </h2>
              <p className="text-slate-600 mb-2">{message}</p>
              {email && (
                <p className="text-emerald-600 font-medium mb-4">{email}</p>
              )}
              {isNewRegistration && countdown > 0 ? (
                <>
                  <p className="text-sm text-slate-500 mb-6">
                    Redirecting to your dashboard in {countdown} seconds...
                  </p>
                  <Button 
                    onClick={handleGoToDashboard}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard Now
                  </Button>
                </>
              ) : (
                <>
                  {!isNewRegistration && (
                    <p className="text-sm text-slate-500 mb-4">
                      You can now close this window or go back to your dashboard.
                    </p>
                  )}
                  <Button 
                    onClick={handleGoToDashboard}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                </>
              )}
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-red-500 to-rose-500 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/login" className="block">
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600"
                    data-testid="back-to-login-btn"
                  >
                    Back to Login
                  </Button>
                </Link>
                <Link to="/register" className="block">
                  <Button variant="outline" className="w-full" data-testid="register-again-btn">
                    Register Again
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
